import { FIREBASE_PUBLIC_KEY } from "config/secrets";
import { getMessaging, getToken } from "firebase/messaging";
import { useCallback, useEffect, useState } from "react";
import app from "utils/firebase";

let cachedToken = null;
let inflight = null;
let messagingInstance = null;

const isSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "Notification" in window;

const getPermission = () =>
  isSupported() ? Notification.permission : "unsupported";

const getMessagingInstance = () => {
  if (!messagingInstance) messagingInstance = getMessaging(app);
  return messagingInstance;
};

const getTokenIfGranted = () => {
  if (cachedToken) return Promise.resolve(cachedToken);
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      );
      await navigator.serviceWorker.ready;

      const token = await getToken(getMessagingInstance(), {
        vapidKey: FIREBASE_PUBLIC_KEY,
        serviceWorkerRegistration: registration,
      });

      cachedToken = token;
      return token;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

const useFCMToken = ({ autoRequest = true } = {}) => {
  const [fcmToken, setFcmToken] = useState(cachedToken);
  const [permission, setPermission] = useState(getPermission);

  useEffect(() => {
    if (!autoRequest || !isSupported()) return;
    if (getPermission() !== "granted") return;
    let cancelled = false;

    getTokenIfGranted()
      .then((token) => {
        if (cancelled || !token) return;
        setFcmToken(token);
      })
      .catch((e) => console.error("FCM init failed:", e));

    return () => {
      cancelled = true;
    };
  }, [autoRequest]);

  const requestToken = useCallback(async () => {
    if (!isSupported()) return { token: null, reason: "unsupported" };

    let perm = Notification.permission;
    if (perm !== "granted") {
      try {
        perm = await Notification.requestPermission();
      } catch {
        perm = "denied";
      }
    }
    setPermission(perm);

    if (perm !== "granted") return { token: null, reason: perm };

    try {
      const token = await getTokenIfGranted();
      if (!token) return { token: null, reason: "error" };
      setFcmToken(token);
      return { token, reason: "granted" };
    } catch (e) {
      console.error("FCM token fetch failed:", e);
      return { token: null, reason: "error" };
    }
  }, []);

  const refreshPermission = useCallback(() => {
    setPermission(getPermission());
  }, []);

  return { fcmToken, permission, requestToken, refreshPermission };
};

export default useFCMToken;
